import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiCopy,
  FiExternalLink,
  FiHelpCircle,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiUsers,
  FiX,
} from "react-icons/fi";
import axios from "../api/axiosInstance";

const LANGUAGE_OPTIONS = [
  ["es", "Spanish"],
  ["en", "English"],
  ["fr", "French"],
  ["it", "Italian"],
  ["de", "German"],
  ["pt", "Portuguese"],
];

const QUESTION_TYPE_OPTIONS = [
  ["rating", "Score 1-5"],
  ["nps", "Recommendation 0-10"],
  ["text", "Free text"],
  ["yes_no", "Yes / No"],
  ["single_choice", "Single choice"],
  ["multi_choice", "Multiple choice"],
];

const METRIC_CODES = [
  "overall_rating",
  "staff",
  "cleanliness",
  "location",
  "comfort",
  "value_for_money",
  "rooms",
  "service",
  "facilities",
  "breakfast",
];

function toQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function normalizeText(value) {
  return String(value || "").trim();
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function formatPercent(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric)}%` : "0%";
}

function slugifyCode(value, fallback = "question") {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || fallback;
}

function createQuestion(index) {
  return {
    local_id: `q-${Date.now()}-${index}`,
    question_text: "",
    question_code: `question_${index}`,
    question_type: "rating",
    is_required: true,
    maps_to_metric_code: index === 1 ? "overall_rating" : "",
    options_text: "",
    scale_max: "5",
  };
}

function createTemplateForm(template = null) {
  if (!template) {
    return {
      name: "",
      is_active: true,
      language_code: "es",
      email_subject: "How was your stay?",
      email_intro_text:
        "Thank you for staying with us. We would love to hear how your experience was.",
      thank_you_message: "Thank you for sharing your feedback with the hotel team.",
      settings: { send_delay_value: "1", send_delay_unit: "days", default_expiration_days: "14" },
      questions: [
        {
          ...createQuestion(1),
          question_text: "How would you rate your stay overall?",
          question_code: "overall_rating",
        },
        {
          ...createQuestion(2),
          question_text: "What went especially well during your stay?",
          question_code: "positive_feedback",
          question_type: "text",
          maps_to_metric_code: "",
        },
        {
          ...createQuestion(3),
          question_text: "What could we improve?",
          question_code: "improvement_feedback",
          question_type: "text",
          maps_to_metric_code: "",
        },
      ],
    };
  }

  return {
    name: template.name || "",
    is_active: Boolean(template.is_active),
    language_code: template.language_code || "es",
    email_subject: template.email_subject || "",
    email_intro_text: template.email_intro_text || "",
    thank_you_message: template.thank_you_message || "",
    settings: {
      send_delay_value: String(template.settings_json?.send_delay_value || ""),
      send_delay_unit: template.settings_json?.send_delay_unit || "days",
      default_expiration_days: String(template.settings_json?.default_expiration_days || "14"),
    },
    questions: Array.isArray(template.questions) && template.questions.length
      ? template.questions.map((question, index) => ({
          local_id: `q-${question.id || index}-${Date.now()}`,
          question_text: question.question_text || "",
          question_code: question.question_code || `question_${index + 1}`,
          question_type: question.question_type || "text",
          is_required: Boolean(question.is_required),
          maps_to_metric_code: question.maps_to_metric_code || "",
          options_text: Array.isArray(question.options_json?.choices)
            ? question.options_json.choices.join("\n")
            : "",
          scale_max: String(question.options_json?.scale_max || "5"),
        }))
      : [createQuestion(1)],
  };
}

function createSendoutForm(templateId = "") {
  return {
    template_id: templateId ? String(templateId) : "",
    reservation_id: "",
    guest_name: "",
    guest_email: "",
    scheduled_at: "",
    expires_at: "",
  };
}

function buildChipClass(prefix, tone) {
  return `${prefix} ${prefix}--${tone}`;
}

function statusTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "answered":
      return "success";
    case "opened":
      return "info";
    case "sent":
      return "warning";
    case "expired":
    case "error":
      return "danger";
    default:
      return "neutral";
  }
}

function statusLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "answered":
      return "Answered";
    case "opened":
      return "Opened";
    case "sent":
      return "Sent";
    case "expired":
      return "Expired";
    case "error":
      return "Error";
    default:
      return "Pending";
  }
}

function sendRuleLabel(template) {
  const settings = template?.settings_json || {};
  const delayValue = Number(settings.send_delay_value || 0);
  const delayUnit = settings.send_delay_unit || "days";
  const expirationDays = Number(settings.default_expiration_days || 0);
  const parts = [];
  parts.push(delayValue > 0 ? `Send ${delayValue} ${delayUnit} after checkout` : "Send manually");
  if (expirationDays > 0) parts.push(`Expires after ${expirationDays} days`);
  return parts.join(" - ");
}

function Modal({ open, title, subtitle, onClose, children }) {
  if (!open) return null;
  return (
    <div className="gfs-modalBackdrop" onClick={onClose}>
      <div className="gfs-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <div className="gfs-modalHeader">
          <div>
            <h2 className="gfs-modalTitle">{title}</h2>
            {subtitle ? <p className="gfs-modalSubtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="gfs-iconBtn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="gfs-modalBody">{children}</div>
      </div>
    </div>
  );
}

function GuestFeedbackSurveysPage() {
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [businessUnit, setBusinessUnit] = useState(null);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sendouts, setSendouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState(createTemplateForm());
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [sendoutForm, setSendoutForm] = useState(createSendoutForm());
  const [sendoutSaving, setSendoutSaving] = useState(false);
  const [sendoutError, setSendoutError] = useState("");
  const [reservationPreview, setReservationPreview] = useState(null);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [createdSendout, setCreatedSendout] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSendoutModal, setShowSendoutModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const fetchOverview = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/surveys/overview${query}`);
      setBusinessUnit(response.data?.business_unit || null);
      setStats(response.data?.stats || null);
      setTemplates(Array.isArray(response.data?.templates) ? response.data.templates : []);
      setSendouts(Array.isArray(response.data?.sendouts) ? response.data.sendouts : []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error loading guest feedback surveys.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setFeedback("");
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const filteredTemplates = useMemo(() => {
    const query = normalizeText(searchQuery).toLowerCase();
    if (!query) return templates;
    return templates.filter((template) =>
      [
        template.name,
        template.email_subject,
        template.language_code,
        (template.questions || []).map((question) => question.question_text).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [searchQuery, templates]);

  const lockedQuestions = Boolean(
    editingTemplateId &&
      templates.find((template) => template.id === editingTemplateId)?.sendouts_count > 0
  );

  const openNewTemplate = () => {
    setEditingTemplateId(null);
    setTemplateForm(createTemplateForm());
    setTemplateError("");
    setShowTemplateModal(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setTemplateForm(createTemplateForm(template));
    setTemplateError("");
    setShowTemplateModal(true);
  };

  const openSendout = (templateId = "") => {
    setSendoutForm(createSendoutForm(templateId));
    setSendoutError("");
    setReservationPreview(null);
    setShowSendoutModal(true);
  };

  const closeModals = () => {
    setShowTemplateModal(false);
    setShowSendoutModal(false);
    setShowHelpModal(false);
    setTemplateError("");
    setSendoutError("");
    setReservationPreview(null);
  };

  const updateQuestion = (index, patch) => {
    setTemplateForm((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question
      ),
    }));
  };

  const buildTemplatePayload = () => ({
    business_unit_id: businessUnitId,
    name: templateForm.name,
    is_active: templateForm.is_active,
    language_code: templateForm.language_code,
    email_subject: templateForm.email_subject,
    email_intro_text: templateForm.email_intro_text,
    thank_you_message: templateForm.thank_you_message,
    settings: {
      send_delay_value: templateForm.settings.send_delay_value || null,
      send_delay_unit: templateForm.settings.send_delay_unit || null,
      default_expiration_days: templateForm.settings.default_expiration_days || null,
    },
    questions: templateForm.questions.map((question, index) => ({
      question_text: question.question_text,
      question_code:
        normalizeText(question.question_code) || slugifyCode(question.question_text, `question_${index + 1}`),
      question_type: question.question_type,
      is_required: question.is_required,
      maps_to_metric_code: question.maps_to_metric_code || null,
      options_text: question.options_text,
      scale_max: question.scale_max || null,
      display_order: index + 1,
    })),
  });

  const saveTemplate = async (event) => {
    event.preventDefault();
    setTemplateSaving(true);
    setTemplateError("");
    try {
      const payload = buildTemplatePayload();
      if (editingTemplateId) {
        await axios.patch(`/api/reviews-intelligence/surveys/templates/${editingTemplateId}`, payload);
        setFeedback("Survey template updated.");
      } else {
        await axios.post("/api/reviews-intelligence/surveys/templates", payload);
        setFeedback("Survey template created.");
      }
      closeModals();
      await fetchOverview({ silent: true });
    } catch (requestError) {
      setTemplateError(requestError?.response?.data?.error || "Error saving the survey template.");
    } finally {
      setTemplateSaving(false);
    }
  };

  const loadReservationPreview = async () => {
    if (!normalizeText(sendoutForm.reservation_id)) {
      setSendoutError("Write the reservation ID first.");
      return;
    }
    setReservationLoading(true);
    setSendoutError("");
    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/surveys/reservations/${encodeURIComponent(sendoutForm.reservation_id)}/preview${query}`);
      const preview = response.data?.reservation || null;
      setReservationPreview(preview);
      setSendoutForm((current) => ({
        ...current,
        guest_name: current.guest_name || preview?.guest_name || "",
        guest_email: current.guest_email || preview?.guest_email || "",
      }));
    } catch (requestError) {
      setReservationPreview(null);
      setSendoutError(requestError?.response?.data?.error || "Error loading reservation preview.");
    } finally {
      setReservationLoading(false);
    }
  };

  const saveSendout = async (event) => {
    event.preventDefault();
    setSendoutSaving(true);
    setSendoutError("");
    try {
      const response = await axios.post("/api/reviews-intelligence/surveys/sendouts", {
        business_unit_id: businessUnitId,
        template_id: sendoutForm.template_id,
        reservation_id: sendoutForm.reservation_id || null,
        guest_name: sendoutForm.guest_name,
        guest_email: sendoutForm.guest_email,
        scheduled_at: sendoutForm.scheduled_at || null,
        expires_at: sendoutForm.expires_at || null,
      });
      setCreatedSendout(response.data?.sendout || null);
      setFeedback("Survey link created.");
      closeModals();
      await fetchOverview({ silent: true });
    } catch (requestError) {
      setSendoutError(requestError?.response?.data?.error || "Error creating the survey link.");
    } finally {
      setSendoutSaving(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setFeedback("Survey link copied to clipboard.");
    } catch (_error) {
      setError("We could not copy the link automatically.");
    }
  };

  const styles = `
    .reservations-container.gfs-pageShell{padding:10px}
    .reservations-container .gfs-shell{background:linear-gradient(180deg,rgba(25,27,53,.94),rgba(11,12,25,.97));border:1px solid rgba(255,255,255,.08);border-radius:22px;box-shadow:0 24px 64px rgba(3,7,18,.32);overflow:hidden}
    .reservations-container .gfs-header{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;padding:18px;border-bottom:1px solid rgba(255,255,255,.08);flex-wrap:wrap}
    .reservations-container .gfs-title{margin:0;font-size:1.4rem;font-weight:800;color:rgba(244,248,255,.98)}
    .reservations-container .gfs-subtitle,.gfs-meta,.gfs-help{font-size:.78rem;line-height:1.6;color:rgba(214,226,247,.68)}
    .reservations-container .gfs-subtitle{margin:6px 0 0;max-width:860px}
    .reservations-container .gfs-hotel{margin-top:8px;font-size:.76rem;color:rgba(214,226,247,.62)}
    .reservations-container .gfs-actions,.gfs-row,.gfs-inlineActions{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .gfs-btn,.gfs-btnGhost{border:1px solid rgba(255,255,255,.14);border-radius:10px;padding:8px 12px;font-size:.78rem;font-weight:700;display:inline-flex;align-items:center;gap:7px}
    .reservations-container .gfs-btn{background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(139,92,246,.28));border-color:rgba(109,184,255,.32);color:rgba(247,250,255,.96)}
    .reservations-container .gfs-btnGhost{background:rgba(255,255,255,.04);color:rgba(244,248,255,.92)}
    .reservations-container .gfs-btn:disabled,.gfs-btnGhost:disabled{opacity:.45;cursor:not-allowed}
    .reservations-container .gfs-notice{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.78rem;line-height:1.55;border:1px solid rgba(255,255,255,.08)}
    .reservations-container .gfs-notice--success{background:rgba(25,164,122,.16);color:rgba(176,246,220,.98);border-color:rgba(25,164,122,.32)}
    .reservations-container .gfs-notice--error{background:rgba(177,48,69,.22);color:rgba(255,213,213,.98);border-color:rgba(255,125,147,.32)}
    .reservations-container .gfs-body{display:flex;flex-direction:column;gap:16px;padding:14px 18px 18px}
    .reservations-container .gfs-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px}
    .reservations-container .gfs-card,.gfs-panel,.gfs-linkBox{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:18px}
    .reservations-container .gfs-card,.gfs-linkBox{padding:16px}
    .reservations-container .gfs-label{font-size:.68rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.62)}
    .reservations-container .gfs-value{margin-top:10px;font-size:1.18rem;font-weight:850;color:rgba(244,248,255,.97)}
    .reservations-container .gfs-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:16px}
    .reservations-container .gfs-panel{padding:16px}
    .reservations-container .gfs-panelHead,.reservations-container .gfs-itemHead{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
    .reservations-container .gfs-panelTitle,.reservations-container .gfs-itemTitle{margin:0;font-size:1rem;font-weight:800;color:rgba(244,248,255,.97)}
    .reservations-container .gfs-itemTitle{font-size:.88rem}
    .reservations-container .gfs-search{position:relative;min-width:260px;flex:1}
    .reservations-container .gfs-search svg{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:rgba(214,226,247,.45)}
    .reservations-container .gfs-input,.reservations-container .gfs-select,.gfs-textarea{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);color:rgba(244,248,255,.94)}
    .reservations-container .gfs-input,.reservations-container .gfs-select{border-radius:10px;height:38px;padding:0 12px;font-size:.78rem;width:100%}
    .reservations-container .gfs-search .gfs-input{padding-left:36px}
    .gfs-textarea{width:100%;min-height:100px;border-radius:14px;padding:12px 14px;font-size:.82rem;line-height:1.6;resize:vertical}
    .reservations-container .gfs-list{display:flex;flex-direction:column;gap:12px}
    .reservations-container .gfs-item,.gfs-question,.gfs-helpCard,.gfs-stat{padding:14px;border-radius:16px;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08)}
    .reservations-container .gfs-subgrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:12px}
    .reservations-container .gfs-statLabel{font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.58);font-weight:800}
    .reservations-container .gfs-statValue{margin-top:6px;font-size:.92rem;font-weight:800;color:rgba(244,248,255,.96);overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .gfs-chip{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:5px 10px;font-size:.68rem;font-weight:800;white-space:nowrap}
    .reservations-container .gfs-chip--neutral{background:rgba(255,255,255,.08);color:rgba(212,224,244,.95)}
    .reservations-container .gfs-chip--success{background:rgba(25,164,122,.25);color:rgba(176,246,220,.98)}
    .reservations-container .gfs-chip--warning{background:rgba(255,193,95,.14);color:rgba(255,230,185,.98)}
    .reservations-container .gfs-chip--danger{background:rgba(177,48,69,.24);color:rgba(255,204,204,.98)}
    .reservations-container .gfs-chip--info{background:rgba(52,152,219,.18);color:rgba(198,230,255,.98)}
    .reservations-container .gfs-empty{padding:28px;text-align:center;font-size:.82rem;line-height:1.7;color:rgba(214,226,247,.72)}
    .gfs-modalBackdrop{position:fixed;inset:0;background:rgba(4,8,18,.68);backdrop-filter:blur(8px);display:flex;justify-content:center;align-items:flex-start;padding:34px 16px;z-index:1200;overflow-y:auto}
    .gfs-modal{width:min(1180px,100%);border-radius:24px;border:1px solid transparent;background:linear-gradient(180deg,rgba(24,24,48,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.45),rgba(168,85,247,.50),rgba(255,77,79,.28)) border-box;box-shadow:0 32px 80px rgba(10,17,20,.46);overflow:hidden;color:rgba(229,237,250,.92)}
    .gfs-modalHeader{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02)}
    .gfs-modalTitle{margin:0;font-size:1.42rem;font-weight:800;color:rgba(244,248,255,.97)}
    .gfs-modalSubtitle{margin:8px 0 0;font-size:.84rem;line-height:1.6;color:rgba(214,226,247,.68);max-width:900px}
    .gfs-iconBtn{width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,236,247,.92);display:inline-flex;justify-content:center;align-items:center;cursor:pointer;font-size:18px}
    .gfs-modalBody{padding:22px 24px 26px}
    .gfs-form{display:flex;flex-direction:column;gap:16px}
    .gfs-formGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    .gfs-formGrid3,.gfs-helpGrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
    .gfs-field{display:flex;flex-direction:column;gap:8px}
    .gfs-fieldLabel{font-size:.74rem;font-weight:700;color:rgba(245,248,255,.95)}
    .gfs-check{display:flex;align-items:center;gap:10px;font-size:.78rem;color:rgba(214,226,247,.76)}
    .gfs-check input{accent-color:#8b5cf6}
    .gfs-footer{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
    .gfs-helpCard h3{margin:0 0 8px;font-size:.86rem;color:rgba(245,248,255,.97)}
    .gfs-helpCard p{margin:0;font-size:.78rem;line-height:1.6;color:rgba(214,226,247,.7)}
    @media (max-width:1180px){.reservations-container .gfs-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.reservations-container .gfs-grid{grid-template-columns:1fr}.reservations-container .gfs-subgrid,.gfs-formGrid3,.gfs-helpGrid{grid-template-columns:1fr 1fr}}
    @media (max-width:860px){.reservations-container.gfs-pageShell{padding:8px}.reservations-container .gfs-header,.reservations-container .gfs-actions,.reservations-container .gfs-panelHead,.reservations-container .gfs-itemHead,.gfs-modalHeader,.gfs-footer{flex-direction:column;align-items:stretch}.reservations-container .gfs-kpis,.reservations-container .gfs-subgrid,.gfs-formGrid,.gfs-formGrid3,.gfs-helpGrid{grid-template-columns:1fr}.gfs-modalBody,.gfs-modalHeader{padding:18px}}
  `;

  return (
    <div className="reservations-container gfs-pageShell">
      <style>{styles}</style>
      <section className="gfs-shell">
        <div className="gfs-header">
          <div>
            <h1 className="gfs-title">Guest Feedback Surveys</h1>
            <p className="gfs-subtitle">
              Create short post-stay surveys, generate shareable links and bring direct guest feedback into the same review intelligence workflow used for public reviews.
            </p>
            {businessUnit?.name ? <div className="gfs-hotel">Current hotel: <strong>{businessUnit.name}</strong></div> : null}
          </div>
          <div className="gfs-actions">
            <button type="button" className="gfs-btn" onClick={openNewTemplate}><FiPlus />New template</button>
            <button type="button" className="gfs-btnGhost" onClick={() => openSendout()}><FiSend />Create survey link</button>
            <button type="button" className="gfs-btnGhost" onClick={() => setShowHelpModal(true)}><FiHelpCircle />How it works</button>
            <button type="button" className="gfs-btnGhost" onClick={() => fetchOverview({ silent: true })} disabled={refreshing || loading}><FiRefreshCw />{refreshing ? "Refreshing..." : "Refresh"}</button>
          </div>
        </div>

        {feedback ? <div className="gfs-notice gfs-notice--success">{feedback}</div> : null}
        {error ? <div className="gfs-notice gfs-notice--error">{error}</div> : null}

        <div className="gfs-body">
          {createdSendout?.public_url ? (
            <div className="gfs-linkBox">
              <div className="gfs-label">Latest survey link created</div>
              <div className="gfs-meta" style={{ overflowWrap: "anywhere", wordBreak: "break-word", marginTop: 8 }}>{createdSendout.public_url}</div>
              <div className="gfs-inlineActions" style={{ marginTop: 12 }}>
                <button type="button" className="gfs-btnGhost" onClick={() => copyLink(createdSendout.public_url)}><FiCopy />Copy link</button>
                <button type="button" className="gfs-btnGhost" onClick={() => window.open(createdSendout.public_url, "_blank", "noopener,noreferrer")}><FiExternalLink />Open public page</button>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="gfs-empty">Loading guest feedback surveys...</div>
          ) : (
            <>
              <div className="gfs-kpis">
                <article className="gfs-card"><div className="gfs-label">Templates</div><div className="gfs-value">{stats?.templates_total ?? 0}</div><div className="gfs-meta">{stats?.active_templates ?? 0} active right now</div></article>
                <article className="gfs-card"><div className="gfs-label">Survey links</div><div className="gfs-value">{stats?.total_sendouts ?? 0}</div><div className="gfs-meta">{stats?.pending_sendouts ?? 0} still pending</div></article>
                <article className="gfs-card"><div className="gfs-label">Opened</div><div className="gfs-value">{stats?.opened_sendouts ?? 0}</div><div className="gfs-meta">Guests who opened the survey</div></article>
                <article className="gfs-card"><div className="gfs-label">Answered</div><div className="gfs-value">{stats?.answered_sendouts ?? 0}</div><div className="gfs-meta">Direct feedback collected</div></article>
                <article className="gfs-card"><div className="gfs-label">Response rate</div><div className="gfs-value">{formatPercent(stats?.response_rate)}</div><div className="gfs-meta">{stats?.expired_sendouts ?? 0} expired without answer</div></article>
              </div>

              <div className="gfs-row">
                <label className="gfs-search">
                  <FiSearch />
                  <input className="gfs-input" type="text" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search templates by name, language or question" />
                </label>
              </div>

              <div className="gfs-grid">
                <section className="gfs-panel">
                  <div className="gfs-panelHead">
                    <div>
                      <div className="gfs-label">Survey templates</div>
                      <h2 className="gfs-panelTitle">Questionnaires ready for guests</h2>
                      <p className="gfs-meta">Build a few clear templates and reuse them whenever the hotel wants direct guest feedback.</p>
                    </div>
                  </div>
                  {filteredTemplates.length ? (
                    <div className="gfs-list">
                      {filteredTemplates.map((template) => {
                        const totalSendouts = Number(template.sendouts_count || 0);
                        const totalAnswered = Number(template.answered_count || 0);
                        const rate = totalSendouts > 0 ? Math.round((totalAnswered / totalSendouts) * 100) : 0;
                        return (
                          <article key={template.id} className="gfs-item">
                            <div className="gfs-itemHead">
                              <div>
                                <div className="gfs-itemTitle">{template.name}</div>
                                <div className="gfs-meta">{template.language_code?.toUpperCase() || "ES"} - {template.questions?.length || 0} questions - {sendRuleLabel(template)}</div>
                              </div>
                              <span className={buildChipClass("gfs-chip", template.is_active ? "success" : "neutral")}>{template.is_active ? "Active" : "Inactive"}</span>
                            </div>
                            {template.email_intro_text ? <div className="gfs-meta" style={{ marginTop: 8 }}>{template.email_intro_text}</div> : null}
                            <div className="gfs-subgrid">
                              <div className="gfs-stat"><div className="gfs-statLabel">Links created</div><div className="gfs-statValue">{totalSendouts}</div></div>
                              <div className="gfs-stat"><div className="gfs-statLabel">Responses</div><div className="gfs-statValue">{totalAnswered}</div></div>
                              <div className="gfs-stat"><div className="gfs-statLabel">Response rate</div><div className="gfs-statValue">{rate}%</div></div>
                            </div>
                            <div className="gfs-meta" style={{ marginTop: 12, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                              First questions: {(template.questions || []).slice(0, 3).map((question) => question.question_text).join(" - ")}
                              {(template.questions || []).length > 3 ? " ..." : ""}
                            </div>
                            <div className="gfs-inlineActions" style={{ marginTop: 12 }}>
                              <button type="button" className="gfs-btnGhost" onClick={() => openEditTemplate(template)}>Edit template</button>
                              <button type="button" className="gfs-btnGhost" onClick={() => openSendout(template.id)}><FiSend />Create link</button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : <div className="gfs-empty">No survey templates yet.<br />Create the first one to start collecting internal feedback.</div>}
                </section>

                <section className="gfs-panel">
                  <div className="gfs-panelHead">
                    <div>
                      <div className="gfs-label">Send history</div>
                      <h2 className="gfs-panelTitle">Recently created survey links</h2>
                      <p className="gfs-meta">The team can copy any recent link again or open the exact public page seen by the guest.</p>
                    </div>
                  </div>
                  {sendouts.length ? (
                    <div className="gfs-list">
                      {sendouts.map((sendout) => (
                        <article key={sendout.id} className="gfs-item">
                          <div className="gfs-itemHead">
                            <div>
                              <div className="gfs-itemTitle">{sendout.guest_name || sendout.guest_email || "Guest survey link"}</div>
                              <div className="gfs-meta">{sendout.template?.name || "Template"} - created {formatDateTime(sendout.created_at)}</div>
                            </div>
                            <span className={buildChipClass("gfs-chip", statusTone(sendout.status))}>{statusLabel(sendout.status)}</span>
                          </div>
                          <div className="gfs-subgrid">
                            <div className="gfs-stat"><div className="gfs-statLabel">Scheduled</div><div className="gfs-statValue">{formatDateTime(sendout.scheduled_at)}</div></div>
                            <div className="gfs-stat"><div className="gfs-statLabel">Opened</div><div className="gfs-statValue">{formatDateTime(sendout.opened_at)}</div></div>
                            <div className="gfs-stat"><div className="gfs-statLabel">Answered</div><div className="gfs-statValue">{formatDateTime(sendout.answered_at)}</div></div>
                          </div>
                          <div className="gfs-inlineActions" style={{ marginTop: 12 }}>
                            <button type="button" className="gfs-btnGhost" onClick={() => copyLink(sendout.public_url)}><FiCopy />Copy link</button>
                            <button type="button" className="gfs-btnGhost" onClick={() => window.open(sendout.public_url, "_blank", "noopener,noreferrer")}><FiExternalLink />Open page</button>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : <div className="gfs-empty">No survey links created yet.<br />Create the first link from any active template.</div>}
                </section>
              </div>
            </>
          )}
        </div>
      </section>

      <Modal open={showTemplateModal} onClose={closeModals} title={editingTemplateId ? "Edit survey template" : "New survey template"} subtitle="Keep the wording short and clear. These questions should help the hotel team understand what went well and what needs action.">
        <form className="gfs-form" onSubmit={saveTemplate}>
          {templateError ? <div className="gfs-notice gfs-notice--error" style={{ margin: 0 }}>{templateError}</div> : null}
          <div className="gfs-formGrid">
            <label className="gfs-field"><span className="gfs-fieldLabel">Template name</span><input className="gfs-input" type="text" value={templateForm.name} onChange={(event) => setTemplateForm((current) => ({ ...current, name: event.target.value }))} placeholder="Example: Post-stay general survey" /></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Language</span><select className="gfs-select" value={templateForm.language_code} onChange={(event) => setTemplateForm((current) => ({ ...current, language_code: event.target.value }))}>{LANGUAGE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <label className="gfs-check"><input type="checkbox" checked={templateForm.is_active} onChange={(event) => setTemplateForm((current) => ({ ...current, is_active: event.target.checked }))} />This template is active and ready to use</label>
          <label className="gfs-field"><span className="gfs-fieldLabel">Invitation subject or internal label</span><input className="gfs-input" type="text" value={templateForm.email_subject} onChange={(event) => setTemplateForm((current) => ({ ...current, email_subject: event.target.value }))} placeholder="Example: Tell us about your recent stay" /></label>
          <label className="gfs-field"><span className="gfs-fieldLabel">Intro text</span><textarea className="gfs-textarea" value={templateForm.email_intro_text} onChange={(event) => setTemplateForm((current) => ({ ...current, email_intro_text: event.target.value }))} /></label>
          <label className="gfs-field"><span className="gfs-fieldLabel">Thank-you message</span><textarea className="gfs-textarea" style={{ minHeight: 84 }} value={templateForm.thank_you_message} onChange={(event) => setTemplateForm((current) => ({ ...current, thank_you_message: event.target.value }))} /></label>
          <div className="gfs-formGrid3">
            <label className="gfs-field"><span className="gfs-fieldLabel">Send delay value</span><input className="gfs-input" type="number" min="0" value={templateForm.settings.send_delay_value} onChange={(event) => setTemplateForm((current) => ({ ...current, settings: { ...current.settings, send_delay_value: event.target.value } }))} /></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Delay unit</span><select className="gfs-select" value={templateForm.settings.send_delay_unit} onChange={(event) => setTemplateForm((current) => ({ ...current, settings: { ...current.settings, send_delay_unit: event.target.value } }))}><option value="hours">Hours</option><option value="days">Days</option></select></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Default expiration in days</span><input className="gfs-input" type="number" min="1" value={templateForm.settings.default_expiration_days} onChange={(event) => setTemplateForm((current) => ({ ...current, settings: { ...current.settings, default_expiration_days: event.target.value } }))} /></label>
          </div>
          {lockedQuestions ? <div className="gfs-notice gfs-notice--error" style={{ margin: 0 }}>This template already has survey links created. You can still edit the surrounding text, but the question structure is locked so past answers stay comparable.</div> : null}
          <div className="gfs-inlineActions">
            {!lockedQuestions ? <button type="button" className="gfs-btnGhost" onClick={() => setTemplateForm((current) => ({ ...current, questions: [...current.questions, createQuestion(current.questions.length + 1)] }))}><FiPlus />Add question</button> : null}
          </div>
          <div className="gfs-list">
            {templateForm.questions.map((question, index) => (
              <div key={question.local_id} className="gfs-question">
                <div className="gfs-itemHead" style={{ marginBottom: 12 }}>
                  <div>
                    <div className="gfs-itemTitle">Question {index + 1}</div>
                    <div className="gfs-meta">{QUESTION_TYPE_OPTIONS.find(([value]) => value === question.question_type)?.[1] || "Question"}</div>
                  </div>
                  {!lockedQuestions && templateForm.questions.length > 1 ? <button type="button" className="gfs-btnGhost" onClick={() => setTemplateForm((current) => ({ ...current, questions: current.questions.filter((_, questionIndex) => questionIndex !== index) }))}><FiX />Remove</button> : null}
                </div>
                <div className="gfs-formGrid">
                  <label className="gfs-field"><span className="gfs-fieldLabel">Question text</span><input className="gfs-input" type="text" disabled={lockedQuestions} value={question.question_text} onChange={(event) => updateQuestion(index, { question_text: event.target.value, question_code: normalizeText(question.question_code) || slugifyCode(event.target.value, `question_${index + 1}`) })} /></label>
                  <label className="gfs-field"><span className="gfs-fieldLabel">Question type</span><select className="gfs-select" disabled={lockedQuestions} value={question.question_type} onChange={(event) => updateQuestion(index, { question_type: event.target.value, options_text: ["single_choice", "multi_choice"].includes(event.target.value) ? question.options_text : "", scale_max: event.target.value === "rating" ? question.scale_max || "5" : event.target.value === "nps" ? "10" : "" })}>{QUESTION_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                </div>
                <div className="gfs-formGrid3" style={{ marginTop: 12 }}>
                  <label className="gfs-field"><span className="gfs-fieldLabel">Stable question code</span><input className="gfs-input" type="text" disabled={lockedQuestions} value={question.question_code} onChange={(event) => updateQuestion(index, { question_code: slugifyCode(event.target.value, `question_${index + 1}`) })} /></label>
                  <label className="gfs-field"><span className="gfs-fieldLabel">Metric mapping</span><input className="gfs-input" type="text" list="gfs-metrics" disabled={lockedQuestions} value={question.maps_to_metric_code} onChange={(event) => updateQuestion(index, { maps_to_metric_code: event.target.value })} placeholder="Optional metric code" /></label>
                  <label className="gfs-check" style={{ alignSelf: "end", minHeight: 38 }}><input type="checkbox" disabled={lockedQuestions} checked={question.is_required} onChange={(event) => updateQuestion(index, { is_required: event.target.checked })} />Guest must answer this question</label>
                </div>
                {question.question_type === "rating" ? <label className="gfs-field" style={{ marginTop: 12 }}><span className="gfs-fieldLabel">Maximum score</span><input className="gfs-input" type="number" min="2" max="10" disabled={lockedQuestions} value={question.scale_max} onChange={(event) => updateQuestion(index, { scale_max: event.target.value })} /></label> : null}
                {["single_choice", "multi_choice"].includes(question.question_type) ? <label className="gfs-field" style={{ marginTop: 12 }}><span className="gfs-fieldLabel">Answer options</span><textarea className="gfs-textarea" disabled={lockedQuestions} style={{ minHeight: 92 }} value={question.options_text} onChange={(event) => updateQuestion(index, { options_text: event.target.value })} placeholder={"Write one option per line\nExample:\nBreakfast\nRoom comfort\nStaff attention"} /></label> : null}
              </div>
            ))}
          </div>
          <datalist id="gfs-metrics">{METRIC_CODES.map((metric) => <option key={metric} value={metric} />)}</datalist>
          <div className="gfs-footer">
            <div className="gfs-help">After saving, this template is ready to create survey links for guests. Submitted answers will also feed review intelligence as internal feedback.</div>
            <div className="gfs-inlineActions">
              <button type="button" className="gfs-btnGhost" onClick={closeModals}>Cancel</button>
              <button type="submit" className="gfs-btn" disabled={templateSaving}>{templateSaving ? "Saving..." : editingTemplateId ? "Save changes" : "Create template"}</button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={showSendoutModal} onClose={closeModals} title="Create survey link" subtitle="Choose the template, optionally load the guest from a reservation and create the public link the guest will answer.">
        <form className="gfs-form" onSubmit={saveSendout}>
          {sendoutError ? <div className="gfs-notice gfs-notice--error" style={{ margin: 0 }}>{sendoutError}</div> : null}
          <div className="gfs-formGrid">
            <label className="gfs-field"><span className="gfs-fieldLabel">Template</span><select className="gfs-select" value={sendoutForm.template_id} onChange={(event) => setSendoutForm((current) => ({ ...current, template_id: event.target.value }))}><option value="">Choose a template</option>{templates.filter((template) => template.is_active).map((template) => <option key={template.id} value={String(template.id)}>{template.name}</option>)}</select></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Reservation ID</span><input className="gfs-input" type="number" min="1" value={sendoutForm.reservation_id} onChange={(event) => setSendoutForm((current) => ({ ...current, reservation_id: event.target.value }))} placeholder="Optional" /></label>
          </div>
          <div className="gfs-inlineActions">
            <button type="button" className="gfs-btnGhost" onClick={loadReservationPreview} disabled={!normalizeText(sendoutForm.reservation_id) || reservationLoading}><FiUsers />{reservationLoading ? "Loading..." : "Load guest from reservation"}</button>
          </div>
          {reservationPreview ? <div className="gfs-linkBox"><div className="gfs-label">Reservation preview</div><div className="gfs-meta">{reservationPreview.guest_name || "No guest name"} - {reservationPreview.guest_email || "No email"} - stay {formatDateTime(reservationPreview.check_in_date)} to {formatDateTime(reservationPreview.check_out_date)}</div></div> : null}
          <div className="gfs-formGrid">
            <label className="gfs-field"><span className="gfs-fieldLabel">Guest name</span><input className="gfs-input" type="text" value={sendoutForm.guest_name} onChange={(event) => setSendoutForm((current) => ({ ...current, guest_name: event.target.value }))} placeholder="Optional if reservation is linked" /></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Guest email</span><input className="gfs-input" type="email" value={sendoutForm.guest_email} onChange={(event) => setSendoutForm((current) => ({ ...current, guest_email: event.target.value }))} placeholder="guest@example.com" /></label>
          </div>
          <div className="gfs-formGrid">
            <label className="gfs-field"><span className="gfs-fieldLabel">Schedule for later</span><input className="gfs-input" type="datetime-local" value={sendoutForm.scheduled_at} onChange={(event) => setSendoutForm((current) => ({ ...current, scheduled_at: event.target.value }))} /></label>
            <label className="gfs-field"><span className="gfs-fieldLabel">Custom expiration</span><input className="gfs-input" type="datetime-local" value={sendoutForm.expires_at} onChange={(event) => setSendoutForm((current) => ({ ...current, expires_at: event.target.value }))} /></label>
          </div>
          <div className="gfs-footer">
            <div className="gfs-help">Once created, the link can be copied, opened or sent through email, WhatsApp or any other channel the team prefers.</div>
            <div className="gfs-inlineActions">
              <button type="button" className="gfs-btnGhost" onClick={closeModals}>Cancel</button>
              <button type="submit" className="gfs-btn" disabled={sendoutSaving}>{sendoutSaving ? "Creating..." : "Create survey link"}</button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={showHelpModal} onClose={closeModals} title="How guest feedback surveys work" subtitle="This guide is written for hotel operations and explains when to create a template, when to create a survey link and what happens after the guest answers.">
        <div className="gfs-helpGrid">
          <article className="gfs-helpCard"><h3>Create one template per use case</h3><p>Use different templates if the hotel needs a general post-stay survey, a family survey or a follow-up in another language.</p></article>
          <article className="gfs-helpCard"><h3>Keep surveys short</h3><p>Start with one overall score, ask what went well and ask what could improve. Only add more questions if the team will truly use the answers.</p></article>
          <article className="gfs-helpCard"><h3>Use reservation prefill when possible</h3><p>Loading the reservation saves time and reduces mistakes in guest name or email before the survey link is created.</p></article>
          <article className="gfs-helpCard"><h3>Answers feed review intelligence</h3><p>When the guest submits the form, the response is stored as internal feedback and enters the same analysis workflow as the hotel reviews.</p></article>
          <article className="gfs-helpCard"><h3>Question structure locks after first send</h3><p>If a template has already been used, its question structure becomes fixed so past responses stay comparable in dashboards and trends.</p></article>
          <article className="gfs-helpCard"><h3>The guest sees a public page</h3><p>The survey link opens a clean public form. The guest does not need to log in and can answer from mobile or desktop.</p></article>
        </div>
        <div className="gfs-footer" style={{ marginTop: 20 }}>
          <div className="gfs-help">This screen is designed so reception, guest experience and operations teams can launch surveys quickly without dealing with technical setup.</div>
          <div className="gfs-inlineActions"><button type="button" className="gfs-btn" onClick={closeModals}>Close help</button></div>
        </div>
      </Modal>
    </div>
  );
}

export default GuestFeedbackSurveysPage;
